import React, { useState } from 'react';
import BlueCallToAction from '../components/common/BlueCallToAction';

const GuidesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Guide categories
  const categories = [
    { id: 'all', name: 'All Guides' },
    { id: 'getting-started', name: 'Getting Started' },
    { id: 'automation', name: 'Automation' },
    { id: 'integration', name: 'Integration' },
    { id: 'advanced', name: 'Advanced' },
    { id: 'best-practices', name: 'Best Practices' }
  ];
  
  // Sample guides data
  const guides = [
    {
      id: 1,
      title: 'Getting Started with Videxa',
      description: 'Learn the basics of setting up and using Videxa for your healthcare organization.',
      category: 'getting-started',
      difficulty: 'Beginner',
      timeToComplete: '15 min',
      author: 'Videxa Team',
      date: 'June 10, 2023',
      image: '/images/guides/getting-started.jpg',
      featured: true
    },
    {
      id: 2,
      title: 'Creating Your First Automation Workflow',
      description: 'Step-by-step guide to creating and deploying your first automation workflow.',
      category: 'automation',
      difficulty: 'Beginner',
      timeToComplete: '20 min',
      author: 'Sarah Chen',
      date: 'May 25, 2023',
      image: '/images/guides/first-workflow.jpg',
      featured: true
    },
    {
      id: 3,
      title: 'Integrating with EHR Systems',
      description: 'Learn how to connect Videxa with your existing healthcare systems and services.',
      category: 'integration',
      difficulty: 'Intermediate',
      timeToComplete: '30 min',
      author: 'Michael Rivera',
      date: 'May 15, 2023',
      image: '/images/guides/integrations.jpg',
      featured: false
    },
    {
      id: 4,
      title: 'Advanced Workflow Techniques',
      description: 'Take your automation to the next level with advanced workflow features.',
      category: 'advanced',
      difficulty: 'Advanced',
      timeToComplete: '45 min',
      author: 'Alex Johnson',
      date: 'April 30, 2023',
      image: '/images/guides/advanced-workflow.jpg',
      featured: false
    },
    {
      id: 5,
      title: 'Healthcare Data Processing with Videxa',
      description: 'Learn how to process and analyze healthcare data using Videxa automation.',
      category: 'automation',
      difficulty: 'Intermediate',
      timeToComplete: '25 min',
      author: 'Emily Watson',
      date: 'April 20, 2023',
      image: '/images/guides/data-processing.jpg',
      featured: false
    },
    {
      id: 6,
      title: 'Security Best Practices',
      description: 'Ensure your automation workflows follow security best practices.',
      category: 'best-practices',
      difficulty: 'Intermediate',
      timeToComplete: '35 min',
      author: 'David Kumar',
      date: 'April 10, 2023',
      image: '/images/guides/security.jpg',
      featured: false
    },
    {
      id: 7,
      title: 'Building Custom Agents',
      description: 'Create your own custom agents to extend the platform\'s capabilities.',
      category: 'advanced',
      difficulty: 'Advanced',
      timeToComplete: '60 min',
      author: 'Jessica Martinez',
      date: 'March 25, 2023',
      image: '/images/guides/custom-agents.jpg',
      featured: false
    },
    {
      id: 8,
      title: 'Optimizing Workflow Performance',
      description: 'Tips and techniques for improving the performance of your automation workflows.',
      category: 'best-practices',
      difficulty: 'Intermediate',
      timeToComplete: '40 min',
      author: 'Robert Kim',
      date: 'March 15, 2023',
      image: '/images/guides/optimization.jpg',
      featured: false
    }
  ];

  // Handle image loading errors
  const handleImageError = (e) => {
    e.target.src = '/images/placeholder.jpg';
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    // In a real application, this would filter the guides based on the search query
    console.log(`Searching for: ${searchQuery}`);
  };

  // Filter guides based on selected category
  const filteredGuides = selectedCategory === 'all' 
    ? guides 
    : guides.filter(guide => guide.category === selectedCategory);

  return (
    <div className="guides-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Healthcare <span className="text-primary-300">Guides</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              Step-by-step tutorials and best practices to help you get the most out of Videxa
            </p>
            <form className="max-w-md mx-auto" onSubmit={handleSearch}>
              <div className="flex">
                <input
                  type="text"
                  placeholder="Search guides..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-l-lg border-0 text-gray-900 focus:ring-2 focus:ring-primary-500"
                />
                <button 
                  type="submit"
                  className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-r-lg transition-colors"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Featured Guides */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">Featured Guides</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {guides.filter(guide => guide.featured).map(guide => (
              <div key={guide.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden shadow-sm">
                <div className="h-48 bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                  <img 
                    src={guide.image} 
                    alt={guide.title} 
                    onError={handleImageError}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-3 py-1 rounded-full text-sm mb-3">
                    {categories.find(cat => cat.id === guide.category)?.name}
                  </span>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">{guide.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{guide.description}</p>
                  <div className="flex justify-between items-center mb-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">{guide.difficulty}</span>
                    <span>{guide.timeToComplete}</span>
                  </div>
                  <a 
                    href={`/guides/${guide.id}`} 
                    className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    View Guide
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Guides */}
      <section className="all-guides">
        <div className="container">
          <div className="guides-header">
            <h2>All Guides</h2>
            <div className="category-filter">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={selectedCategory === category.id ? 'active' : ''}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          <div className="guides-grid">
            {filteredGuides.map(guide => (
              <div className="guide-card" key={guide.id}>
                <div className="guide-image">
                  <img 
                    src={guide.image} 
                    alt={guide.title} 
                    onError={handleImageError}
                  />
                  <span className="difficulty-badge">{guide.difficulty}</span>
                </div>
                <div className="guide-content">
                  <span className="guide-category">{categories.find(cat => cat.id === guide.category)?.name}</span>
                  <h3>{guide.title}</h3>
                  <p>{guide.description}</p>
                  <div className="guide-meta">
                    <span className="author">By {guide.author}</span>
                    <span className="time">{guide.timeToComplete}</span>
                  </div>
                  <a href={`/guides/${guide.id}`} className="read-more">Read Guide →</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Paths */}
      <section className="learning-paths">
        <div className="container">
          <h2>Learning Paths</h2>
          <p>Follow these curated learning paths to master Agent Nexus</p>
          
          <div className="paths-grid">
            <div className="path-card">
              <div className="path-icon">
                <i className="icon-beginner"></i>
              </div>
              <h3>Beginner Path</h3>
              <p>Start your journey with the fundamentals of Videxa</p>
              <ul className="path-steps">
                <li>Getting Started with Videxa</li>
                <li>Creating Your First Automation Workflow</li>
                <li>Basic Integration Setup</li>
              </ul>
              <span className="text-gray-500 text-sm">Coming soon</span>
            </div>
            
            <div className="path-card">
              <div className="path-icon">
                <i className="icon-intermediate"></i>
              </div>
              <h3>Intermediate Path</h3>
              <p>Enhance your skills with more advanced features</p>
              <ul className="path-steps">
                <li>Advanced Workflow Techniques</li>
                <li>Healthcare Data Processing with Videxa</li>
                <li>Optimizing Workflow Performance</li>
              </ul>
              <span className="text-gray-500 text-sm">Coming soon</span>
            </div>
            
            <div className="path-card">
              <div className="path-icon">
                <i className="icon-advanced"></i>
              </div>
              <h3>Advanced Path</h3>
              <p>Master complex automation and customization</p>
              <ul className="path-steps">
                <li>Building Custom Agents</li>
                <li>Enterprise Integration Patterns</li>
                <li>Advanced Security and Compliance</li>
              </ul>
              <span className="text-gray-500 text-sm">Coming soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Community Contributions */}
      <section className="community-guides">
        <div className="container">
          <h2>Community Contributions</h2>
          <p>Guides and tutorials created by the Videxa community</p>
          
          <div className="community-grid">
            <div className="community-card">
              <div className="community-author">
                <img src="/images/avatars/user1.jpg" alt="User Avatar" onError={handleImageError} />
                <span>Thomas Wright</span>
              </div>
              <h3>Automating Patient Support with Videxa</h3>
              <p>How we reduced response times by 75% using intelligent healthcare automation</p>
              <span className="text-gray-500 text-sm">Coming soon</span>
            </div>
            
            <div className="community-card">
              <div className="community-author">
                <img src="/images/avatars/user2.jpg" alt="User Avatar" onError={handleImageError} />
                <span>Priya Sharma</span>
              </div>
              <h3>Creating a Multi-step Approval Workflow</h3>
              <p>A detailed walkthrough of building complex approval processes</p>
              <span className="text-gray-500 text-sm">Coming soon</span>
            </div>
            
            <div className="community-card">
              <div className="community-author">
                <img src="/images/avatars/user3.jpg" alt="User Avatar" onError={handleImageError} />
                <span>Carlos Rodriguez</span>
              </div>
              <h3>Integrating Videxa with Legacy Healthcare Systems</h3>
              <p>Strategies for connecting modern healthcare automation with older infrastructure</p>
              <span className="text-gray-500 text-sm">Coming soon</span>
            </div>
          </div>
          
          <div className="contribute-cta">
            <h3>Share Your Knowledge</h3>
            <p>Have you created an innovative healthcare solution with Videxa? Share your guide with the community!</p>
            <span className="text-gray-500">Guide submission coming soon</span>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to put these guides into practice?"
        description="Start implementing Videxa in your healthcare organization today and begin optimizing your operations."
        primaryButtonText="Request Demo"
        primaryButtonLink="/demo"
        secondaryButtonText="Contact Sales"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default GuidesPage; 